// src/pages/Attacks.js
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';

// ----------------------------------------------------------------------
// 1) Helpers
// ----------------------------------------------------------------------
function formatDateTime(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',  // e.g. "Dec"
    day: 'numeric',  // e.g. "2"
    year: 'numeric', // e.g. "2024"
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });
}

function formatBps(bps) {
  if (!bps || bps < 0) return 'N/A';
  const absBps = Math.abs(bps);

  if (absBps < 1e3) {
    return `${bps.toFixed(2)} bps`;
  } else if (absBps < 1e6) {
    return `${(bps / 1e3).toFixed(2)} Kbps`;
  } else if (absBps < 1e9) {
    return `${(bps / 1e6).toFixed(2)} Mbps`;
  } else if (absBps < 1e12) {
    return `${(bps / 1e9).toFixed(2)} Gbps`;
  } else {
    return `${(bps / 1e12).toFixed(2)} Tbps`;
  }
}

function formatPps(pps) {
  if (!pps || pps < 0) return 'N/A';
  if (pps < 1_000_000) {
    return pps.toLocaleString(); // e.g. "807,300"
  } else if (pps < 1_000_000_000) {
    return `${(pps / 1_000_000).toFixed(1)}M`; // e.g. "2.3M"
  } else {
    return `${(pps / 1_000_000_000).toFixed(1)}B`; // e.g. "2.3B"
  }
}

// ----------------------------------------------------------------------
// 2) InfoIcon: an SVG icon + tooltip
//    - We allow tooltip alignment via a `position` prop ("left" or "right").
// ----------------------------------------------------------------------
function InfoIcon({ message, position = 'right' }) {
  return (
    <div className="relative inline-block group ml-1 cursor-pointer text-blue-400">
      <svg
        className="h-5 w-5 fill-current"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2" />
        <path
          fill="currentColor"
          d="M12 7a1.25 1.25 0 110 2.5A1.25 1.25 0 0112 7zm0 3.75a.75.75 0 00-.75.75v3.5c0 .414.336.75.75.75h.008a.75.75 0 00.742-.648l.008-.102v-3.5a.75.75 0 00-.75-.75H12z"
        />
      </svg>
      {/* Tooltip container */}
      <div
        className={`
          hidden
          group-hover:block
          absolute
          bg-gray-700
          text-white
          text-sm
          rounded
          px-2
          py-1
          w-64
          z-50
          pointer-events-none
          ${
            position === 'right'
              ? 'left-full ml-2 top-1/2 -translate-y-1/2'
              : 'right-full mr-2 top-1/2 -translate-y-1/2'
          }
        `}
        style={{ whiteSpace: 'normal' }}
      >
        {message}
      </div>
    </div>
  );
}

function Attacks() {
  const navigate = useNavigate();
  const storedUser = localStorage.getItem('user');
  const userObj = storedUser ? JSON.parse(storedUser) : null;
  const token = userObj?.token || '';

  const [attacks, setAttacks] = useState([]);
  const [loading, setLoading] = useState(true);

  // ---- Filter states ----
  const [searchIP, setSearchIP] = useState('');
  const [searchReason, setSearchReason] = useState('');
  const [startDate, setStartDate] = useState(''); // "YYYY-MM-DD"
  const [endDate, setEndDate] = useState('');   // "YYYY-MM-DD"

  // ---- Sorting states ----
  // We'll store which field to sort by and which direction (asc or desc).
  const [sortField, setSortField] = useState('start');
  const [sortOrder, setSortOrder] = useState('desc'); // or 'asc'

  // ---- Pagination states ----
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9

  // Fetch from API
  const fetchAttacks = useCallback(async () => {
    try {
      const response = await fetch(
        'https://backend.hybridprotect.hybridhosting.co/api/v1/account/attacks',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch attacks.');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setAttacks(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching attacks:', error);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAttacks();
  }, [token, navigate, fetchAttacks]);

  // ---- Derived array: filter, then sort, then paginate ----
  function getFilteredSortedAttacks() {
    // 1) Filter
    let filtered = attacks.filter((attack) => {
      const ipMatch = attack.ip_address
        .toLowerCase()
        .includes(searchIP.toLowerCase());

      const reasonMatch = attack.reason
        .toLowerCase()
        .includes(searchReason.toLowerCase());

      // Date filtering: check if attack.start is within [startDate, endDate]
      // We'll parse the date from attack.start, compare with user inputs.
      let dateInRange = true;
      if (startDate) {
        const attackStart = new Date(attack.start).getTime();
        const filterStart = new Date(`${startDate}T00:00:00`).getTime();
        if (attackStart < filterStart) {
          dateInRange = false;
        }
      }
      if (endDate) {
        const attackStart = new Date(attack.start).getTime();
        const filterEnd = new Date(`${endDate}T23:59:59`).getTime();
        if (attackStart > filterEnd) {
          dateInRange = false;
        }
      }

      return ipMatch && reasonMatch && dateInRange;
    });

    // 2) Sort
    filtered.sort((a, b) => {
      // Decide which value to compare
      let valA, valB;

      switch (sortField) {
        case 'start':
          valA = new Date(a.start).getTime();
          valB = new Date(b.start).getTime();
          break;
        case 'end':
          valA = new Date(a.end).getTime();
          valB = new Date(b.end).getTime();
          break;
        case 'attack_size':
          valA = a.peak_bps;
          valB = b.peak_bps;
          break;
        case 'peak_pps':
          valA = a.peak_pps;
          valB = b.peak_pps;
          break;
        case 'ip':
          // Compare strings
          valA = a.ip_address.toLowerCase();
          valB = b.ip_address.toLowerCase();
          break;
        case 'reason':
          valA = a.reason.toLowerCase();
          valB = b.reason.toLowerCase();
          break;
        default:
          valA = new Date(a.start).getTime();
          valB = new Date(b.start).getTime();
      }

      if (valA < valB) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }

  // Pagination helpers
  const totalAttacks = getFilteredSortedAttacks().length;
  const totalPages = Math.ceil(totalAttacks / pageSize);

  const currentAttacks = getFilteredSortedAttacks().slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  function goToPage(pageNum) {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  }

  function handleNextPage() {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }

  function handlePrevPage() {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }

  // If filters, sorting, or page changes, we want to reset to page 1 sometimes.
  // But for simplicity, only do so if you want. 
  // We'll keep it so the user stays on the same page if they change filters.

  return (
    <Layout>
      <div className="p-8 text-white">
        <h1 className="text-4xl font-bold mb-8">DDoS Attacks</h1>

        {/* ========== FILTER / SEARCH / SORT CONTROLS ========== */}
        <div className="bg-dark-card p-4 rounded-lg mb-4">
          <h2 className="text-xl font-semibold mb-2">Filters & Sorting</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Search by IP */}
            <div>
              <label className="block mb-1">Search IP</label>
              <input
                type="text"
                className="w-full p-2 rounded bg-[#272E3B] border border-gray-600 text-white"
                value={searchIP}
                onChange={(e) => {
                  setSearchIP(e.target.value);
                  setCurrentPage(1); // optional: reset to page 1
                }}
                placeholder="e.g. 1.2.3.4"
              />
            </div>

            {/* Search by Reason */}
            <div>
              <label className="block mb-1">Search Reason</label>
              <input
                type="text"
                className="w-full p-2 rounded bg-[#272E3B] border border-gray-600 text-white"
                value={searchReason}
                onChange={(e) => {
                  setSearchReason(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="e.g. threshold"
              />
            </div>

            {/* Date Range: Start */}
            <div>
              <label className="block mb-1">From (Start Date)</label>
              <input
                type="date"
                className="w-full p-2 rounded bg-[#272E3B] border border-gray-600 text-white"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Date Range: End */}
            <div>
              <label className="block mb-1">To (End Date)</label>
              <input
                type="date"
                className="w-full p-2 rounded bg-[#272E3B] border border-gray-600 text-white"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Sort By */}
            <div>
              <label className="block mb-1">Sort By</label>
              <select
                className="w-full p-2 rounded bg-[#272E3B] border border-gray-600 text-white"
                value={sortField}
                onChange={(e) => {
                  setSortField(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="start">Start Time</option>
                <option value="end">End Time</option>
                <option value="attack_size">Attack Size</option>
                <option value="peak_pps">Peak PPS</option>
                <option value="ip">IP Address</option>
                <option value="reason">Reason</option>
              </select>
            </div>

            {/* Sort Order */}
            <div>
              <label className="block mb-1">Sort Order</label>
              <select
                className="w-full p-2 rounded bg-[#272E3B] border border-gray-600 text-white"
                value={sortOrder}
                onChange={(e) => {
                  setSortOrder(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>

        {/* ========== ATTACKS TABLE ========== */}
        <div className="bg-dark-card rounded-lg shadow-md p-4 overflow-visible">
          {loading ? (
            <p>Loading attacks...</p>
          ) : currentAttacks.length === 0 ? (
            <p>No attacks found for your filters.</p>
          ) : (
            <table className="w-full relative">
              <thead>
                <tr className="border-b border-custom-gray">
                  <th className="text-left p-2">IP Address</th>
                  <th className="text-left p-2">
                    Reason
                    {/* Re-adding InfoIcon for "Reason" if needed */}
                  </th>
                  <th className="text-left p-2">Start Time</th>
                  <th className="text-left p-2">End Time</th>
                  <th className="text-left p-2">
                    Attack Size
                    <InfoIcon
                      message="Highest observed data rate (bits per second), auto-converted to Kbps, Mbps, Gbps, or Tbps."
                      position="right"
                    />
                  </th>
                  <th className="text-left p-2">
                    Peak PPS
                    <InfoIcon
                      message="Maximum packets per second observed. Large values are shown in short notation (e.g., 1.2M)."
                      position="left"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentAttacks.map((attack) => {
                  // Re-adding InfoIcon for specific reasons
                  let reasonLabel = attack.reason;
                  let reasonInfo = '';

                  if (attack.reason === 'packet threshold reached') {
                    reasonLabel = 'Packet Threshold Reached';
                    reasonInfo =
                      "Triggered when our systems detect an unusual surge in packet volume. Once this dynamic threshold is reached, we begin filtering the attack.";
                  } else if (attack.reason === 'byte threshold reached') {
                    reasonLabel = 'Byte Threshold Reached';
                    reasonInfo =
                      "Triggered when our systems detect an unusual surge in data volume. Once this dynamic threshold is reached, we begin filtering the attack.";
                  }

                  return (
                    <tr key={attack.id} className="border-b border-custom-gray">
                      <td className="p-2">{attack.ip_address}</td>
                      <td className="p-2 flex items-center">
                        {/* Reason label */}
                        <span>{reasonLabel}</span>
                        {/* If there's a reason tooltip, show the icon. */}
                        {reasonInfo && (
                          <InfoIcon
                            message={reasonInfo}
                            position="right" 
                          />
                        )}
                      </td>
                      <td className="p-2">{formatDateTime(attack.start)}</td>
                      <td className="p-2">{formatDateTime(attack.end)}</td>
                      <td className="p-2">{formatBps(attack.peak_bps)}</td>
                      <td className="p-2">{formatPps(attack.peak_pps)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ========== PAGINATION CONTROLS ========== */}
        {!loading && totalAttacks > 0 && (
          <div className="flex items-center justify-between mt-4">
            {/* Prev / Next */}
            <div>
              <button
                className="mr-2 px-3 py-1 bg-gray-700 rounded hover:bg-gray-600"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <button
                className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
            {/* Page Info and GoToPage */}
            <div>
              <span className="mr-4">
                Page {currentPage} of {totalPages}
              </span>
              <span>
                Go to page:
                <input
                  type="number"
                  className="w-16 ml-2 p-1 rounded bg-[#272E3B] border border-gray-600 text-white"
                  onKeyDown={(e) => {
                    // If Enter key is pressed
                    if (e.key === 'Enter') {
                      const pageNum = parseInt(e.target.value, 10);
                      if (!isNaN(pageNum)) {
                        goToPage(pageNum);
                        e.target.value = '';
                      }
                    }
                  }}
                />
              </span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Attacks;